import {MISC_KEYS} from "@/api/miscApi";
import {useUpdateUserLanguageMutation} from "@/api/userApi";
import BlockInfluencerModal from "@/components/BlockedInfluencers/BlockInfluencerModal";
import CancelApplicationModal from "@/components/Campaign/CancelApplicationModal";
import DeclineCancellationRequestModal from "@/components/Campaign/DeclineCancellationRequestModal";
import ExtendCampaignModal from "@/components/Campaign/ExtendCampaignModal";
import ConfirmCampaignEndingModal from "@/components/Campaign/common/ConfirmCampaignEndingModal";
import CreateCampaignModal from "@/components/Campaign/management/CreateCampaignModal";
import RefundInfluencerModal from "@/components/Campaign/management/RefundInfluencerModal";
import CampaignDeleteDialog from "@/components/Campaign/management/campaignOverview/CampaignDeleteDialog";
import CommunityCodesCsvModal from "@/components/Campaign/management/components/CommunityCodesCsvModal";
import ContentDownloader from "@/components/Campaign/management/components/ContentDownloader";
import Carousel from "@/components/Campaign/management/components/carousel/Carousel";
import RateInfluencersModal from "@/components/Campaign/management/reportingTab/RateInfluencersModal";
import FeedbackModal from "@/components/Common/FeedbackModal";
import FullPageLoader from "@/components/Common/FullPageLoader";
import IENotSupportedModal from "@/components/Common/IENotSupportedModal";
import SendProductModal from "@/components/Common/Influencer/InfluencerCard/PreparationCard/SendProductModal";
import InfluencerAppRedirectCta from "@/components/Common/InfluencerAppRedirectCta";
import NotFound from "@/components/Common/NotFound";
import PaymentFailedModal from "@/components/Common/PaymentFailedModal";
import DeleteFilterModal from "@/components/Common/filter/DeleteFilterModal";
import DiscardFilterChangesModal from "@/components/Common/filter/DiscardFilterChangesModal";
import ConfirmEmailModal from "@/components/Common/modal/ConfirmEmailModal";
import AmbassadorActionModal from "@/components/Common/referral/AmbassadorActionModal";
import AmbassadorProgramModal from "@/components/Common/referral/AmbassadorProgramModal";
import InviteAmbassadorsModal from "@/components/Common/referral/InviteAmbassadorsModal";
import PublishShareNudgeModal from "@/components/Common/referral/PublishShareNudgeModal";
import EditProfileModal from "@/components/Company/EditProfileModal";
import OverrideSubmissionModal from "@/components/ContentApproval/OverrideSubmissionModal";
import RejectContentModal from "@/components/ContentApproval/RejectContentModal";
import RequestTakedownModal from "@/components/ContentApproval/RequestTakedownModal";
import RevertFromAiContentApprovalModal from "@/components/ContentApproval/RevertFromAiContentApprovalModal";
import ReviewContentModal from "@/components/ContentApproval/ReviewContentModal";
import SetAiAutonomyModal from "@/components/ContentApproval/SetAiAutonomyModal";
import SwitchToAiContentApprovalModal from "@/components/ContentApproval/SwitchToAiContentApprovalModal";
import CustomerSuccessModal from "@/components/CustomerSuccess/CustomerSuccessModal";
import Footer from "@/components/Footer";
import AmbassadorRequestModal from "@/components/Influencer/InfluencerDetail/AmbassadorRequestModal";
import AgencyInviteModal from "@/components/MultiAccount/AgencyInviteModal";
import InviteModal from "@/components/MultiAccount/InviteModal";
import InviteWrongAccountModal from "@/components/MultiAccount/InviteWrongAccountModal";
import RegisterCompanyMemberModal from "@/components/MultiAccount/RegisterCompanyMemberModal";
import RequestPermissionsModal from "@/components/MultiAccount/RequestPermissionsModal";
import WelcomeToMultiAccountModal from "@/components/MultiAccount/WelcomeToMultiAccountModal";
import BrandImpersonationBar from "@/components/NavBar/BrandImpersonationBar";
import NavbarLoggedOut from "@/components/NavBar/NavbarLoggedOut";
import Sidebar from "@/components/NavBar/Sidebar";
import TopBar from "@/components/NavBar/TopBar";
import DiscardSettingsChangesModal from "@/components/Settings/DiscardSettingsChangesModal";
import SettingsModal from "@/components/Settings/SettingsModal";
import AddTiktokToPlanModal from "@/components/TiktokSearch/AddTiktokToPlanModal";
import ManageProductLinesModal from "@/components/TiktokSearch/ManageProductLinesModal";
import PurchasePlanModal from "@/components/TiktokSearch/PurchasePlanModal";
import DisciplinaryModal from "@/components/discipline/DisciplinaryModal";
import SingleDisciplineModal from "@/components/discipline/SingleDisciplineModal";
import FloatingBuyoutMenu from "@/components/plans/FloatingBuyoutMenu";
import type {CategorySerializer} from "@/generated/DjangoTypes";
import {useChatSocket} from "@/hooks/useChatSocket";
import {useDateConfig} from "@/hooks/useDateConfig";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useInternalIntercom} from "@/hooks/useInternalIntercom";
import useLocationEffect from "@/hooks/useLocationEffect";
import {usePosthogIdentify} from "@/hooks/usePosthogIdentify";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import useStripeSetupReturn from "@/hooks/useStripeSetupReturn";
import {useTheme} from "@/hooks/useTheme";
import {useTiktokCountryScan} from "@/hooks/useTiktokCountryScan";
import {useIsAdmin, useIsCompany, useIsInfuencer, useIsLoggedIn} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import ErrorPage from "@/pages/ErrorPage";
import UnderMaintenancePage from "@/pages/UnderMaintenancePage";
import CreateCustomerModal from "@/pages/agencies/CreateCustomerModal";
import AddMoreBuyoutsModal from "@/pages/plans/AddMoreBuyoutsModal";
import AddMoreInvitesModal from "@/pages/plans/AddMoreInvitesModal";
import RequestMoreModal from "@/pages/plans/RequestMoreModal";
import ManageCreditsModal from "@/pages/plans/creditSystem/ManageCreditsModal";
import RegistrationModal from "@/pages/registerCompany/RegistrationModal";
import InvalidCompanyUrlModal from "@/pages/welcome/InvalidCompanyUrlModal";
import {Can} from "@/permissions";
import {posthog} from "@/posthog";
import {useAdminCarouselStore, useCarouselStore} from "@/store/carousel";
import {useCategoryStore} from "@/store/category";
import {LANGUAGE_OPTIONS, ROUTES_WITHOUT_FOOTER, ROUTES_WITHOUT_NAVBAR} from "@/store/constants";
import {
  acceptLanguageAtom,
  ambassadorActionModalAtom,
  ambassadorProgramModalAtom,
  ambassadorRequestModalAtom,
  blockInfluencerModalAtom,
  brandImpersonationAtom,
  campaignDeleteAtom,
  cancelApplicationModalDataAtom,
  confirmCampaignEndingModalAtom,
  createCampaignForCompanyAtom,
  createCustomerModalOpenAtom,
  customerSuccessModalAgencyIdAtom,
  declineCancellationRequestModalDataAtom,
  deleteFilterModalAtom,
  discardFilterChangesModalAtom,
  discardSettingsChangesModalAtom,
  extendCampaignModalDataAtom,
  inviteAmbassadorsModalAtom,
  inviteWrongAccountModalAtom,
  isAddTiktokToPlanModalOpenAtom,
  isAdminFeedbackModalOpenAtom,
  isAgencyInviteModalOpenAtom,
  isCommunityCodesCsvModalOpenAtom,
  isConfirmEmailModalOpenAtom,
  isDisciplinaryModalOpenAtom,
  isEditProfileModalOpenAtom,
  isFeedbackModalOpenAtom,
  isFinishRegistrationModalVisibleAtom,
  isInvalidCompanyUrlModalOpenAtom,
  isMainSidebarCollapsedAtom,
  isManageProductLinesModalOpenAtom,
  isMoreBuyoutsModalOpenAtom,
  isMoreInviteModalOpenAtom,
  isPurchasePlanModalOpenAtom,
  isRateInfluencersModalOpenAtom,
  isRegisterCompanyMemberModalOpenAtom,
  isRequestMoreResourcesModalOpenAtom,
  isRequestPermissionsModalOpenAtom,
  isSingleDisciplineModalOpenAtom,
  isWelcomeToMultiAccountModalOpenAtom,
  languagePreferenceAtom,
  manageCreditsModalAtom,
  multiAccountInviteModalAtom,
  overrideSubmissionModalAtom,
  paymentFailedModalDataAtom,
  publishShareNudgeModalAtom,
  purchaseModalContentAtom,
  refundInfluencerModalDataAtom,
  rejectContentModalAtom,
  requestTakedownModalAtom,
  revertFromAiContentApprovalModalAtom,
  reviewContentModalAtom,
  sendProductModalDataAtom,
  setAiAutonomyModalAtom,
  settingsModalAtom,
  switchToAiContentApprovalModalAtom,
} from "@/store/jotaiAtoms";
import {ModalContext} from "@/store/modalContext";
import useRegistrationStore from "@/store/registration";
import type {TLanguage} from "@/store/types";
import useUserStore from "@/store/user";
import {CookieBanner} from "@palmabit/react-cookie-law";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Outlet, createRootRoute, useLocation, useRouterState, useSearch} from "@tanstack/react-router";
import type {AxiosResponse} from "axios";
import dayjs from "dayjs";
import {useAtomValue, useSetAtom} from "jotai";
import Cookies from "js-cookie";
import {Suspense, useContext, useEffect, useRef} from "react";
import {ErrorBoundary} from "react-error-boundary";
import ReactPixel from "react-facebook-pixel";
import ReactGA from "react-ga4";
import {useTranslation} from "react-i18next";
import {useEvent, useMeasure, usePrevious} from "react-use";

const COOKIE_CONSENT_NAMES = [
  "rcl_consent_given",
  "rcl_marketing_consent",
  "rcl_preferences_consent",
  "rcl_statistics_consent",
];
const ROUTES_WITHOUT_INFLUENCER_CTA = ["/verify-account", "/verify-email-change", "/join/$slug", "/embed/join/$slug"];

export const Route = createRootRoute({
  component: RootLayout,
  notFoundComponent: () => <NotFound />,
});

function RootLayout() {
  const {t, i18n} = useTranslation("common");
  const location = useLocation();
  const queryClient = useQueryClient();
  const {error} = useFetchUser();
  const isLoggedIn = useIsLoggedIn();
  const isAdmin = useIsAdmin();
  const isCompany = useIsCompany();
  const isInfluencer = useIsInfuencer({excludeAdmins: true});
  const user = useUserStore(state => state.user);
  const categorySetter = useCategoryStore(state => state.setter);
  const isMarketingScriptLoaded = useRef(false);
  const isStatisticsScriptLoaded = useRef(false);

  const ambassadorActionModal = useAtomValue(ambassadorActionModalAtom);
  const ambassadorRequestModalData = useAtomValue(ambassadorRequestModalAtom);
  const blockInfluencerModalData = useAtomValue(blockInfluencerModalAtom);
  const brandImpersonation = useAtomValue(brandImpersonationAtom);
  const campaignEndingModalData = useAtomValue(confirmCampaignEndingModalAtom);
  const campaignToBeDeleted = useAtomValue(campaignDeleteAtom);
  const cancelApplicationModalData = useAtomValue(cancelApplicationModalDataAtom);
  const companyForCampaignCreation = useAtomValue(createCampaignForCompanyAtom);
  const declineCancellationRequestModalData = useAtomValue(declineCancellationRequestModalDataAtom);
  const switchToAiContentApprovalModal = useAtomValue(switchToAiContentApprovalModalAtom);
  const setAiAutonomyModal = useAtomValue(setAiAutonomyModalAtom);
  const revertFromAiContentApprovalModal = useAtomValue(revertFromAiContentApprovalModalAtom);
  const deleteFilterModalData = useAtomValue(deleteFilterModalAtom);
  const discardFilterChangesModalData = useAtomValue(discardFilterChangesModalAtom);
  const discardSettingsChangesModalData = useAtomValue(discardSettingsChangesModalAtom);
  const extendCampaignModalData = useAtomValue(extendCampaignModalDataAtom);
  const isAddTiktokToPlanModalOpen = useAtomValue(isAddTiktokToPlanModalOpenAtom);
  const isAdminFeedbackModalOpen = useAtomValue(isAdminFeedbackModalOpenAtom);
  const isAgencyInviteModalOpen = useAtomValue(isAgencyInviteModalOpenAtom);
  const isCommunityCodesCsvModalOpen = useAtomValue(isCommunityCodesCsvModalOpenAtom);
  const isConfirmEmailModalOpen = useAtomValue(isConfirmEmailModalOpenAtom);
  const isCreateCustomerModalOpen = useAtomValue(createCustomerModalOpenAtom);
  const customerSuccessModalAgencyId = useAtomValue(customerSuccessModalAgencyIdAtom);
  const isDisciplinaryModalOpen = useAtomValue(isDisciplinaryModalOpenAtom);
  const isEditProfileModalOpen = useAtomValue(isEditProfileModalOpenAtom);
  const isFeedbackModalOpen = useAtomValue(isFeedbackModalOpenAtom);
  const isFinishRegistrationModalOpen = useAtomValue(isFinishRegistrationModalVisibleAtom);
  const isInvalidCompanyUrlModalOpen = useAtomValue(isInvalidCompanyUrlModalOpenAtom);
  const isManageProductLinesModalOpen = useAtomValue(isManageProductLinesModalOpenAtom);
  const isMoreBuyoutsModalOpen = useAtomValue(isMoreBuyoutsModalOpenAtom);
  const isMoreInviteModalOpen = useAtomValue(isMoreInviteModalOpenAtom);
  const isPurchasePlanModalOpen = useAtomValue(isPurchasePlanModalOpenAtom);
  const isRateInfluencersModalOpen = useAtomValue(isRateInfluencersModalOpenAtom);
  const isRegisterCompanyMemberModalOpen = useAtomValue(isRegisterCompanyMemberModalOpenAtom);
  const inviteWrongAccountModal = useAtomValue(inviteWrongAccountModalAtom);
  const isAmbassadorProgramModal = useAtomValue(ambassadorProgramModalAtom);
  const isInviteAmbassadorsModal = useAtomValue(inviteAmbassadorsModalAtom);
  const isRequestMoreResourcesOpenAtom = useAtomValue(isRequestMoreResourcesModalOpenAtom);
  const isRequestPermissionModalOpen = useAtomValue(isRequestPermissionsModalOpenAtom);
  const isSingleDisciplineModalOpen = useAtomValue(isSingleDisciplineModalOpenAtom);
  const settingsModal = useAtomValue(settingsModalAtom);
  const isWelcomeToMultiAccountModalOpen = useAtomValue(isWelcomeToMultiAccountModalOpenAtom);
  const manageCreditsModal = useAtomValue(manageCreditsModalAtom);
  const multiAccountInviteModal = useAtomValue(multiAccountInviteModalAtom);
  const paymentFailedModalData = useAtomValue(paymentFailedModalDataAtom);
  const publishShareNudge = useAtomValue(publishShareNudgeModalAtom);
  const purchaseModalContent = useAtomValue(purchaseModalContentAtom);
  const refundInfluencerModalData = useAtomValue(refundInfluencerModalDataAtom);
  const overrideSubmissionModal = useAtomValue(overrideSubmissionModalAtom);
  const rejectContentModal = useAtomValue(rejectContentModalAtom);
  const requestTakedownModal = useAtomValue(requestTakedownModalAtom);
  const reviewContentModal = useAtomValue(reviewContentModalAtom);
  const sendProductModalData = useAtomValue(sendProductModalDataAtom);
  const setAcceptLanguage = useSetAtom(acceptLanguageAtom);
  const setPaymentFailedModalData = useSetAtom(paymentFailedModalDataAtom);
  const {payment_failed} = useSearch({strict: false});
  const storedLanguage = useAtomValue(languagePreferenceAtom);

  const carouselStore = isAdminFeedbackModalOpen ? useAdminCarouselStore : useCarouselStore;
  const isCarouselOpen = carouselStore(state => state.isOpen);
  const isIEModalOpen = carouselStore(state => state.isIENotSupportedModalOpen);
  const isBackendDown = error?.code === "ERR_NETWORK";
  const getIsUnderMaintenance = () => {
    const maintenanceDeadline = import.meta.env.VITE_APP_UNDER_MAINTENANCE_UNTIL_UTC;
    return !!maintenanceDeadline && dayjs().isBefore(maintenanceDeadline);
  };
  const {data: categoriesData} = useQuery<
    AxiosResponse<{
      maximum_allowed_category: number;
      results: CategorySerializer[];
      version: number;
    }>
  >({queryKey: MISC_KEYS.qCategoryList});
  const {data: adminCategoriesData} = useGenericQuery<CategorySerializer[]>({
    queryKey: MISC_KEYS.qCategoryListAdmin,
    enabled: isAdmin,
  });
  const {mutate: updateLanguage} = useUpdateUserLanguageMutation();

  useEffect(() => {
    const cookie = Cookies.get("isLoggedIn");

    if (isLoggedIn && !cookie) {
      Cookies.set("isLoggedIn", "true", {expires: 365});
    }

    if (!isLoggedIn && cookie) {
      Cookies.remove("isLoggedIn");
    }
  }, [isLoggedIn]);
  useEffect(() => {
    categorySetter(state => {
      state.categories = categoriesData?.data.results ?? [];
      state.maxAllowedCategories = categoriesData?.data.maximum_allowed_category!;
    });
  }, [categoriesData]);
  useEffect(() => {
    categorySetter(state => {
      state.adminCategories = adminCategoriesData ?? [];
    });
  }, [adminCategoriesData]);
  useEffect(() => {
    const browserLanguage: TLanguage = navigator.language.startsWith("de") ? "de_CH" : "en_EN";
    const userLanguage = user.language_code;
    const preferredLanguageOption = LANGUAGE_OPTIONS.find(
      lang => lang.code === (userLanguage ?? storedLanguage ?? browserLanguage),
    );

    i18n.changeLanguage(preferredLanguageOption?.i18nextCode);
    setAcceptLanguage(preferredLanguageOption?.i18nextCode === "ger" ? "de" : preferredLanguageOption?.i18nextCode!);
    useRegistrationStore.getState().setValue("languageCode", preferredLanguageOption?.code);

    if (user.id && userLanguage !== preferredLanguageOption?.code) {
      updateLanguage({
        lang: LANGUAGE_OPTIONS.find(lang => lang.i18nextCode === i18n.resolvedLanguage)?.code as TLanguage,
      });
    }
  }, [storedLanguage, user.id, user.language_code, updateLanguage]);
  useEffect(() => {
    [MISC_KEYS.qCategoryList, MISC_KEYS.qCategoryListAdmin].map(queryKey => queryClient.invalidateQueries({queryKey}));
  }, [storedLanguage]);

  useEffect(() => {
    if (payment_failed && user.payment_failed_invoices?.length) {
      setPaymentFailedModalData(user.payment_failed_invoices);
    }
  }, [payment_failed, user.payment_failed_invoices]);

  useChatSocket();
  useDateConfig();
  useTheme();
  useEvent("wheel", _ => {
    // Prevent scrolling on wheel event in input[type="number"]
    const input = document.activeElement as HTMLInputElement;

    if (input.type === "number") {
      input.blur();
    }
  });
  useInternalIntercom();
  useLocationEffect();
  usePosthogIdentify();
  useStripeSetupReturn();
  useTiktokCountryScan({enabled: isLoggedIn && !isInfluencer, variant: "switch"});
  useTiktokCountryScan({enabled: isLoggedIn && !isInfluencer, variant: "add"});

  const isSidebarCollapsed = useAtomValue(isMainSidebarCollapsedAtom);
  const {modalIds} = useContext(ModalContext);
  const isModalOpen = !!modalIds.length;
  const wasModalOpen = usePrevious(isModalOpen);
  const [cookieBannerRef, {height: cookieBannerHeight}] = useMeasure<HTMLDivElement>();
  useEffect(() => {
    document.documentElement.style.setProperty("--cookie-banner-height", `${cookieBannerHeight}px`);
  }, [cookieBannerHeight]);
  const showNavbar = !ROUTES_WITHOUT_NAVBAR.some(route => location.pathname.includes(route));
  const showFooter = !ROUTES_WITHOUT_FOOTER.some(route => location.pathname.includes(route));
  const isRouteWithoutCookieBanner = location.pathname.includes("/embed/");
  const isRouteWithoutInfluencerCta = useRouterState({
    select: state =>
      state.matches.some(
        match => match.routeId.startsWith("/_unauthenticated") || ROUTES_WITHOUT_INFLUENCER_CTA.includes(match.routeId),
      ),
  });
  const showInfluencerRedirectCta = isInfluencer && !isRouteWithoutInfluencerCta;
  const showSidebar = showNavbar && isLoggedIn && !getIsUnderMaintenance() && !isInfluencer;
  const showLoggedOutNavbar = showNavbar && !isLoggedIn && !getIsUnderMaintenance();
  const showBrandImpersonationBar = showSidebar && !!brandImpersonation;

  return (
    <div className="font-roboto flex min-h-screen flex-col justify-between">
      <Can I="create" a="BuyoutPurchase">
        <div
          className={`pointer-events-none fixed bottom-[calc(1rem+var(--cookie-banner-height))] z-2000 flex items-end gap-2 transition-[bottom] duration-200 max-sm:left-1/2 max-sm:w-full max-sm:-translate-x-1/2 max-sm:justify-between max-sm:px-3 sm:gap-4 ${showSidebar ? (isSidebarCollapsed ? "sm:left-4 md:left-18" : "sm:left-4 md:left-54") : "left-1/2 sm:left-8"}`}>
          <FloatingBuyoutMenu place="top-end" type="seats" />
          {!isCompany && <FloatingBuyoutMenu type="campaigns" />}
          <FloatingBuyoutMenu place="top-end" type="buyouts" />
          <FloatingBuyoutMenu type="credits" />
        </div>
      </Can>
      {!import.meta.env.VITE_IS_E2E && !isRouteWithoutCookieBanner && (
        <div
          className={clsxm(
            "fixed right-0 bottom-0 left-0 z-100000",
            showSidebar &&
              (isModalOpen
                ? `bg-neutral-white-dark ${isSidebarCollapsed ? "md:pl-14" : "md:pl-50"}`
                : `${wasModalOpen ? "" : "transition-[left] duration-200"} ${isSidebarCollapsed ? "md:left-14" : "md:left-50"}`),
          )}
          ref={cookieBannerRef}>
          <CookieBanner
            className="bg-neutral-white-dark static"
            message={t("cookieBanner")}
            policyLink="https://refluenced.com/cookie-policy/"
            privacyPolicyLinkText={t("privacy_policy")}
            necessaryOptionText={t("Necessary")}
            preferencesOptionText={t("Preferences")}
            statisticsOptionText={t("Statistics")}
            marketingOptionText={t("Marketing")}
            acceptButtonText={t("Accept All")}
            acceptSelectionButtonText={t("Accept Selection")}
            showDeclineButton={true}
            dismissOnScroll={false}
            showPreferencesOption={true}
            showStatisticsOption={true}
            showMarketingOption={true}
            preferencesDefaultChecked={true}
            statisticsDefaultChecked={true}
            marketingDefaultChecked={true}
            wholeDomain={true}
            onAccept={() => {
              // react-cookie-law ignores `wholeDomain` (it passes the path under the wrong option key), so its
              // consent cookies only apply below the directory they were accepted on - elsewhere the banner reads
              // them as missing and declines statistics, opting PostHog out. Re-write them site-wide; this runs
              // before the banner reads the per-category cookies.
              COOKIE_CONSENT_NAMES.forEach(name => {
                const value = Cookies.get(name);

                if (value) {
                  Cookies.set(name, value, {expires: 365, path: "/"});
                }
              });
            }}
            onAcceptStatistics={() => {
              posthog.opt_in_capturing({captureEventName: false});

              if (isStatisticsScriptLoaded.current) {
                return;
              }

              isStatisticsScriptLoaded.current = true;

              ReactGA.initialize(import.meta.env.VITE_APP_GOOGLE_ANALYTICS_TRACKING_ID ?? "G-BGK6J4X5N9");
              (window as Record<string, any>)[
                `ga-disable-${import.meta.env.VITE_APP_GOOGLE_ANALYTICS_TRACKING_ID ?? "G-BGK6J4X5N9"}`
              ] = false;

              ReactPixel.init("1371835213256071", undefined, {autoConfig: true, debug: false});
              ReactPixel.pageView();
            }}
            onDeclineStatistics={() => posthog.opt_out_capturing()}
            onAcceptMarketing={() => {
              if (isMarketingScriptLoaded.current) {
                return;
              }

              isMarketingScriptLoaded.current = true;

              const gtm = document.createElement("script");
              gtm.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-K4Z8MS5');`;
              document.head.appendChild(gtm);

              const gtm_noscript = document.createElement("noscript");
              gtm_noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-K4Z8MS5"
        height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
              document.body.insertBefore(gtm_noscript, document.body.firstChild);
            }}
          />
        </div>
      )}
      {showBrandImpersonationBar && <BrandImpersonationBar impersonation={brandImpersonation} />}
      {showSidebar && <Sidebar />}
      {ambassadorActionModal.isOpen && <AmbassadorActionModal />}
      {ambassadorRequestModalData && <AmbassadorRequestModal />}
      {blockInfluencerModalData && <BlockInfluencerModal />}
      {campaignEndingModalData.isOpen && <ConfirmCampaignEndingModal />}
      {campaignToBeDeleted && <CampaignDeleteDialog />}
      {cancelApplicationModalData && <CancelApplicationModal />}
      {companyForCampaignCreation && <CreateCampaignModal />}
      {declineCancellationRequestModalData && <DeclineCancellationRequestModal />}
      {deleteFilterModalData && <DeleteFilterModal />}
      {discardFilterChangesModalData && <DiscardFilterChangesModal />}
      {extendCampaignModalData && <ExtendCampaignModal />}
      {isAddTiktokToPlanModalOpen && <AddTiktokToPlanModal />}
      {isAgencyInviteModalOpen && <AgencyInviteModal />}
      {isCommunityCodesCsvModalOpen && <CommunityCodesCsvModal />}
      {isAmbassadorProgramModal.isOpen && <AmbassadorProgramModal />}
      {isInviteAmbassadorsModal.isOpen && <InviteAmbassadorsModal />}
      {isCarouselOpen && <Carousel />}
      {isCreateCustomerModalOpen && <CreateCustomerModal />}
      {customerSuccessModalAgencyId !== null && <CustomerSuccessModal />}
      {isConfirmEmailModalOpen && <ConfirmEmailModal />}
      {isDisciplinaryModalOpen && <DisciplinaryModal />}
      {isEditProfileModalOpen && <EditProfileModal />}
      {isFeedbackModalOpen && <FeedbackModal />}
      {isFinishRegistrationModalOpen && !inviteWrongAccountModal && <RegistrationModal />}
      {isIEModalOpen && <IENotSupportedModal />}
      {isInvalidCompanyUrlModalOpen && <InvalidCompanyUrlModal />}
      {isManageProductLinesModalOpen && <ManageProductLinesModal />}
      {isMoreBuyoutsModalOpen.show && <AddMoreBuyoutsModal />}
      {isMoreInviteModalOpen && <AddMoreInvitesModal />}
      {isPurchasePlanModalOpen && <PurchasePlanModal />}
      {isRateInfluencersModalOpen && <RateInfluencersModal />}
      {isRegisterCompanyMemberModalOpen && <RegisterCompanyMemberModal />}
      {!!inviteWrongAccountModal && <InviteWrongAccountModal />}
      {isRequestMoreResourcesOpenAtom.show && <RequestMoreModal />}
      {isRequestPermissionModalOpen && <RequestPermissionsModal />}
      {settingsModal && <SettingsModal />}
      {discardSettingsChangesModalData && <DiscardSettingsChangesModal />}
      {isSingleDisciplineModalOpen && <SingleDisciplineModal />}
      {isWelcomeToMultiAccountModalOpen && <WelcomeToMultiAccountModal />}
      {manageCreditsModal.isOpen && <ManageCreditsModal />}
      {multiAccountInviteModal.isVisible && <InviteModal />}
      {paymentFailedModalData && <PaymentFailedModal />}
      {publishShareNudge.isOpen && <PublishShareNudgeModal />}
      {purchaseModalContent.contents.length !== 0 && <ContentDownloader />}
      {refundInfluencerModalData.isOpen && <RefundInfluencerModal />}
      {rejectContentModal.isOpen && <RejectContentModal />}
      {reviewContentModal.isOpen && <ReviewContentModal />}
      {!!overrideSubmissionModal && <OverrideSubmissionModal />}
      {!!switchToAiContentApprovalModal && <SwitchToAiContentApprovalModal />}
      {!!setAiAutonomyModal && <SetAiAutonomyModal />}
      {!!revertFromAiContentApprovalModal && <RevertFromAiContentApprovalModal />}
      {!!requestTakedownModal && <RequestTakedownModal />}
      {sendProductModalData && <SendProductModal />}
      <div
        className={clsxm(
          showSidebar
            ? `${isSidebarCollapsed ? "md:ml-14" : "md:ml-50"} shadow-content-elevated relative z-1100 flex min-h-screen flex-col bg-white transition-[margin] duration-200`
            : "flex min-h-screen flex-col",
          showBrandImpersonationBar && "pt-14",
        )}>
        {showSidebar && <TopBar />}
        {showLoggedOutNavbar && <NavbarLoggedOut />}
        <main
          lang={i18n.resolvedLanguage === "ger" ? "de" : i18n.resolvedLanguage}
          className="flex grow flex-col [&>div]:w-full">
          <ErrorBoundary FallbackComponent={ErrorPage}>
            <Suspense fallback={<FullPageLoader />}>
              {getIsUnderMaintenance() || isBackendDown ? (
                <UnderMaintenancePage />
              ) : showInfluencerRedirectCta ? (
                <InfluencerAppRedirectCta />
              ) : (
                <Outlet />
              )}
            </Suspense>
          </ErrorBoundary>
        </main>
        {showFooter && !showInfluencerRedirectCta && <Footer />}
      </div>
    </div>
  );
}
